import { useCallback } from 'react';
import { showApiError } from '@/lib/errors/showApiError';
import { useToast } from '@/hooks/use-toast';
import { subscriptionsApi } from '@/lib/api';
import { friendlyMessage } from '@/lib/errors';
import { awaitSubscriptionAccess } from '@/lib/billing/awaitSubscriptionAccess';

declare global {
  interface Window {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped vendor SDK */
    Razorpay: any;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped vendor SDK */
    Cashfree: any;
  }
}

/**
 * Gateway checkout payload as the backend returns it. Fields are gateway
 * specific — Razorpay sends subscription_id/order_id, Cashfree a
 * payment_session_id — so every consumer must null-check what it reads.
 */
export interface CheckoutData {
  gateway?: string;
  key?: string;
  name?: string;
  description?: string;
  prefill?: Record<string, unknown>;
  subscription_id?: string;
  order_id?: string;
  amount?: number;
  currency?: string;
  payment_session_id?: string;
  environment?: string;
}

/**
 * Shared subscription checkout.
 *
 * This is the single implementation of "turn a plan id into money" — the
 * Razorpay/Cashfree script loading, the gateway modals and the post-payment
 * reload. It was lifted verbatim out of pages/Billing.tsx so the billing page
 * and the trial-expired gate drive the SAME flow; do not re-implement either
 * gateway in a component.
 */

export const loadRazorpayScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout script'));
    document.body.appendChild(script);
  });
};

const loadCashfreeScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Cashfree checkout script'));
    document.body.appendChild(script);
  });
};

export interface UseSubscriptionCheckoutOptions {
  /** Gateway to prefer; falls back to the gateway config default. */
  gateway?: string | null;
}

/** What Razorpay hands the success callback. */
interface RazorpayHandlerResponse {
  razorpay_payment_id?: string;
  razorpay_subscription_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

export function useSubscriptionCheckout(options: UseSubscriptionCheckoutOptions = {}) {
  const { toast } = useToast();
  const { gateway } = options;

  const handleRazorpayCheckout = useCallback(
    async (checkoutData: CheckoutData, _subscriptionId?: string) => {
      try {
        await loadRazorpayScript();

        const isSubscription = !!checkoutData.subscription_id;
        const isOrder = !!checkoutData.order_id;

        const razorpayOptions: Record<string, unknown> = {
          key: checkoutData.key,
          name: checkoutData.name || 'Subscription Payment',
          description: checkoutData.description || 'Complete your subscription payment',
          prefill: checkoutData.prefill || {},
          theme: {
            color: '#6366f1', // Primary color
          },
          /*
           * 🔴 THIS CALLBACK MUST TELL THE SERVER. It previously took no
           * arguments and only showed a toast, so the three values Razorpay
           * passes here — payment id, subscription id and signature — were
           * discarded and `/subscriptions/verify` was never called. That left
           * webhooks as the ONLY way a payment could be recorded, so any
           * customer whose webhook was delayed or unreachable paid and stayed
           * locked out.
           */
          handler: async function (response: RazorpayHandlerResponse) {
            if (isSubscription && response?.razorpay_payment_id) {
              const verified = await subscriptionsApi.verifyPayment({
                razorpaySubscriptionId:
                  response.razorpay_subscription_id || checkoutData.subscription_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              });
              if (!verified.success) {
                /*
                 * The money is taken either way — say so, and do not pretend
                 * it failed. The webhook and the status reconciliation are
                 * both still able to finish the job.
                 */
                toast({
                  title: 'Payment received',
                  description:
                    "We're still confirming it with your bank. This page will update shortly.",
                });
              }
            }

            if (!isSubscription) {
              toast({ variant: 'success', title: 'Success!', description: 'Your payment was successful.' });
              setTimeout(() => {
                window.location.reload();
              }, 1500);
              return;
            }

            /*
             * Reload only once the SERVER grants access, not on a timer. A fixed
             * 1.5s reload raced activation: if verify or the webhook had not
             * landed yet, the page came back to the expired gate even though
             * the customer had paid.
             */
            toast({ title: 'Payment received', description: 'Activating your plan…' });
            // Wait for THIS subscription, not just "any access": a customer
            // upgrading from a live trial already has access, so the default
            // check would pass instantly and reload them onto the trial plan.
            const active = await awaitSubscriptionAccess(
              _subscriptionId
                ? { until: (sub) => sub.has_access !== false && sub.id === _subscriptionId }
                : undefined,
            );
            if (active) {
              toast({ variant: 'success', title: 'Success!', description: 'Your plan is active.' });
            } else {
              toast({
                title: 'Payment received',
                description: "We're still confirming it with your bank. Your plan will activate automatically.",
              });
            }
            window.location.reload();
          },
          modal: {
            /*
             * UPI completes on the customer's PHONE, so the handler above may
             * never fire — they close the sheet, pay, and nothing tells us.
             * Asking the server to re-check gives it a chance to confirm with
             * Razorpay directly (see _reconcileRazorpaySubscription) rather
             * than leaving a paid customer looking unpaid.
             */
            ondismiss: async function () {
              const localId = _subscriptionId;
              if (localId) {
                /*
                 * UPI settles on the phone a few seconds AFTER the sheet closes,
                 * so one immediate check reported "Cancelled" to customers who
                 * then paid. Keep asking the server (each call makes it
                 * reconcile with Razorpay directly) for a short window.
                 */
                const deadline = Date.now() + 30_000;
                for (;;) {
                  const status = await subscriptionsApi.checkSubscriptionStatus(localId);
                  const s = status.data?.status;
                  if (s === 'active' || s === 'trialing') {
                    toast({
                      variant: 'success',
                      title: 'Payment confirmed',
                      description: 'Your subscription is now active.',
                    });
                    // This subscription, not "any access" (see the handler above).
                    await awaitSubscriptionAccess({
                      timeoutMs: 10_000,
                      until: (sub) => sub.has_access !== false && sub.id === localId,
                    });
                    window.location.reload();
                    return;
                  }
                  if (Date.now() >= deadline) break;
                  await new Promise((resolve) => setTimeout(resolve, 3_000));
                }
              }
              toast({
                variant: 'success',
                title: 'Cancelled',
                description:
                  'You can complete the authentication/payment later from the billing page.',
              });
            },
          },
        };

        if (isSubscription) {
          razorpayOptions.subscription_id = checkoutData.subscription_id;
        } else if (isOrder) {
          razorpayOptions.order_id = checkoutData.order_id;
          razorpayOptions.amount = checkoutData.amount;
          razorpayOptions.currency = checkoutData.currency || 'INR';
          razorpayOptions.method = {
            upi: true,
            card: true,
            netbanking: true,
            wallet: true,
            emi: false,
          };
        } else {
          throw new Error('Invalid checkout data: missing subscription_id or order_id');
        }

        const razorpay = new window.Razorpay(razorpayOptions);
        razorpay.open();
      } catch (error) {
        showApiError(error, {
          fallback: {
            title: 'Error',
            description: 'Failed to open payment gateway. Please try again.',
          },
        });
      }
    },
    [toast],
  );

  const handleCashfreeCheckout = useCallback(
    async (checkoutData: CheckoutData, _subscriptionId?: string) => {
      try {
        if (!checkoutData.payment_session_id) {
          throw new Error('Invalid checkout data — missing payment session.');
        }

        await loadCashfreeScript();
        const cashfree = window.Cashfree({
          mode: checkoutData.environment === 'production' ? 'production' : 'sandbox',
        });

        let result: { error?: { message?: string }; paymentDetails?: unknown } | undefined;
        try {
          result = await cashfree.checkout({
            paymentSessionId: checkoutData.payment_session_id,
            redirectTarget: '_modal',
          });
        } catch {
          toast({
            variant: 'info',
            title: 'Payment Cancelled',
            description: 'You closed the payment window. You can try again anytime.',
          });
          return;
        }

        if (result?.error) {
          toast({
            title: 'Payment Failed',
            description: result.error.message || 'Payment could not be completed.',
            variant: 'destructive',
          });
        } else if (result?.paymentDetails) {
          try {
            await subscriptionsApi.verifyPayment({
              gateway: 'cashfree',
              cashfreeOrderId: checkoutData.order_id,
            });
          } catch {
            /* webhook will handle it */
          }

          // Same rule as Razorpay: reload once the server grants access.
          toast({ title: 'Payment received', description: 'Activating your plan…' });
          // THIS subscription, not "any access" — a customer on a live trial
          // already has access, so the default check passed instantly and
          // reloaded them onto the trial plan (same fix as the Razorpay path).
          const active = await awaitSubscriptionAccess(
            _subscriptionId
              ? { until: (sub) => sub.has_access !== false && sub.id === _subscriptionId }
              : undefined,
          );
          toast(
            active
              ? { title: 'Payment Successful!', description: 'Your plan is active.', variant: 'success' }
              : { title: 'Payment received', description: "We're still confirming it. Your plan will activate automatically." },
          );
          window.location.reload();
        } else {
          toast({
            variant: 'info',
            title: 'Payment Cancelled',
            description: 'You closed the payment window. You can try again anytime.',
          });
        }
      } catch (error) {
        showApiError(error, {
          fallback: { title: 'Error', description: 'Failed to open Cashfree checkout.' },
        });
      }
    },
    [toast],
  );

  /**
   * Create a checkout session for `planId` and hand off to the right gateway.
   * Resolves once the gateway modal has been opened (or the flow short-circuited).
   */
  const startCheckout = useCallback(
    async (planId: string, gatewayOverride?: string) => {
      const gwToUse = gatewayOverride || gateway || undefined;

      const result = await subscriptionsApi.createCheckoutSession(planId, gwToUse);
      if (result.error) {
        const rawCode = result.code || result.error;
        const gatewayUnavailable = rawCode === 'PAYMENT_GATEWAY_ERROR';
        toast({
          title: "Couldn't upgrade your plan",
          description: gatewayUnavailable
            ? "We couldn't process your subscription at the moment because our payment service is temporarily unavailable. Please try again in a few minutes. If the issue continues, contact support."
            : friendlyMessage(
                { error: result.error, code: result.code },
                "We couldn't start checkout. Please try again, or contact support.",
              ),
          variant: 'destructive',
        });
        return;
      }

      if (result.data?.message && result.data.message.includes('Free plan activated')) {
        toast({
          variant: 'success',
          title: 'Success!',
          description: 'Free plan activated. You can now start using the platform.',
        });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        return;
      }

      const checkoutData = result.data?.checkoutData;

      if (!checkoutData) {
        toast({
          variant: 'success',
          title: 'Success',
          description: result.data?.message || 'Subscription created successfully.',
        });
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      if (checkoutData.gateway === 'cashfree') {
        handleCashfreeCheckout(checkoutData, result.data?.subscriptionId);
      } else {
        if (checkoutData.subscription_id || checkoutData.order_id) {
          handleRazorpayCheckout(checkoutData, result.data?.subscriptionId);
        } else {
          toast({
            variant: 'success',
            title: 'Subscription Created',
            description:
              'Your subscription has been created. Please check your email for payment instructions.',
          });
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }
    },
    [gateway, toast, handleCashfreeCheckout, handleRazorpayCheckout],
  );

  return { startCheckout, handleRazorpayCheckout, handleCashfreeCheckout };
}
